/* eslint-disable class-methods-use-this */
import { RxSocketUrls } from '@Constants/CommonConstants';
import UserDatabases from '@Services/UserDatabases';
import isObject from 'lodash/isObject';
import forEach from 'lodash/forEach';
import { checkCasNumberSearchErrorPayload, updateCasNumberFromSearchPayload } from '@Containers/CasNumber/actions';
import { decamelize } from '@Utils/Camelize';
import SocketBroker from '@Services/SocketBroker';
import store from '@Store';
import { query as querySelector, searchId as searchIdSelector } from '@Pages/QuickSearch/Components/QuickResults/QuickResultsSelectors';

class QuickResultsService {
  previewResultsTemp = [];

  /**
   * @name subscription
   * @param {function} callback
   * @param {object} data
   * @methodOf setupWebSockets
   */
  _subscription = (callback, data) => {
    const { payload } = data;
    const searchId = searchIdSelector(store.getState());
    const queryString = querySelector(store.getState());

    // Stop execution if no payload present
    if (!payload || !isObject(payload)) {
      return;
    }

    if (this.queryString !== queryString) {
      this.previewResultsTemp = [];
      this.queryString = queryString;
    }

    if (Array.isArray(payload.items)) {
      if (payload.searchId === searchId) {
        this._memorizeItemsResults(payload.items);
      }

      Logger.info('WS preview response:', data);
    } else {
      const quickResult = this.previewResultsTemp?.find((item) => item.queryId === payload.queryId);

      if (quickResult) {
        quickResult.resultName = payload.resultName;
        quickResult.resultsCount = payload.resultSize;
        quickResult.hitSetKey = payload.hitSetKey;
        quickResult.searchId = payload.searchId;
        quickResult.recordCount = payload.recordCount; // Patent Family Clustering
        if (payload.casRn) {
          quickResult.resolvedByCasApi = true;
        }

        // CAS Search - QS
        updateCasNumberFromSearchPayload(payload);

        Logger.info(
          `${payload.resultSize} results found`,
          `queryId:${payload.queryId}`,
          `searchId:${payload.searchId}`,
          'Description:',
          quickResult.description,
          'DatabaseId:',
          quickResult.databaseId,
          data
        );
      }
    }

    callback(this.previewResultsTemp, data);
  };

  setupWebSockets(callback) {
    return SocketBroker.observe(RxSocketUrls.ChannelsUrls.SearchInfo, this._subscription.bind(null, callback));
  }

  // eslint-disable-next-line class-methods-use-this
  setupProcessInfoChannel(successCallback) {
    return SocketBroker.observe(RxSocketUrls.ChannelsUrls.ProgressInfo, successCallback);
  }

  // eslint-disable-next-line class-methods-use-this
  subscribeWSErrors(errorCallback) {
    return SocketBroker.observe(RxSocketUrls.ChannelsUrls.QueueErrors, (data) => {
      if (!data) {
        return;
      }

      if (data.exception) {
        errorCallback(data.exception);
      }

      // CAS Search error
      checkCasNumberSearchErrorPayload(data.payload);
    });
  }

  /**
   * @name generateIndexes
   * @methodOf memorizeItemsResults
   */
  _generateIndexes = (indexes) => {
    const cleanIndexes = [];
    forEach(indexes, (index) => {
      if (index.text !== ' ' && index.type !== 'whitespace') {
        if (!index.type) {
          // eslint-disable-next-line no-param-reassign
          index.type = 'ignored';
        }
        cleanIndexes.push(index);
      }
    });
    return cleanIndexes;
  };

  /**
   * @name getDatabaseLabel
   * @methodOf memorizeItemsResults
   */
  _getDatabaseLabel = (databaseId) => (UserDatabases.isDefaultDatabase(databaseId) ? '' : UserDatabases.getDbName(databaseId));

  /**
   * @ngdoc method
   * @name memorizeItemsResults
   * @methodOf subscription
   * @returns {Number} Total number of items
   */
  _memorizeItemsResults(items) {
    this.previewResultsTemp = [];
    forEach(items, (resultPreview) => {
      // create an interface with logging on dev in case properties are null
      this.previewResultsTemp?.push({
        title: decamelize(resultPreview.context),
        context: resultPreview.context,
        databaseId: resultPreview.databaseId,
        databaseLabel: this._getDatabaseLabel(resultPreview.databaseId),
        queryId: resultPreview.queryId,
        indexes: this._generateIndexes(resultPreview.indexes),
        whereClauseImportToken: resultPreview.whereClauseImportToken,
        semanticSearchIncluded: resultPreview.semanticSearchIncluded,
        semanticResultName: resultPreview.semanticResultName,
        // Regex to fix for string without whitespaces,
        // like: 'aspirin, titanium, else'
        description: resultPreview.description?.replace(/([,;])/g, '$1 '),
        feedbacks: resultPreview.feedbacks,
        groupId: resultPreview.groupId,
        isCollapsed: true,
      });
    });
    return items.length;
  }
}
export default new QuickResultsService();
